/**
 * EditableCell Component
 *
 * Inline cell editing for table explorer edit mode.
 * Supports double-click or F2 to enter edit mode, Escape to cancel, Enter to confirm.
 * For JSON/multiline content, opens expanded edit dialog instead.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Maximize2 } from 'lucide-react';
import { Input } from '../../../../components/ui/input';
import { Checkbox } from '../../../../components/ui/checkbox';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import {
  shouldExpandToDialog,
  shouldShowExpandIcon,
  detectContentMode,
  type ExpandedEditMode,
} from '../utils/expandedEditDetection';
import { safeToDatetimeLocal, safeToLocaleString } from '../../../../utils/dateUtils';
import { resolveCellEditVariant } from '../../common/components/cell-edit/cellEditVariant';
import { SmartCellInput } from '../../common/components/cell-edit/SmartCellInput';
import type { LooseRelationship } from '../../../../types/electron';
import type { TableExplorerCellValueCodec, TableExplorerRelationshipPort } from './tableExplorerHostPorts';
import { CellParseError } from './CellParseError';

/**
 * Per-cell context that lets EditableCell pick a smart editor variant (enum /
 * FK). Optional — when absent, EditableCell uses its existing dataType-driven
 * inputs. Both grids thread the same shape so the variant decision (in
 * `resolveCellEditVariant`) stays consistent across surfaces.
 */
export type EditableCellContext = {
  connectionId: string;
  database: string;
  engine: string;
  /** The column's source schema (where it physically lives). */
  schema: string;
  /** The column's source table. */
  table: string;
  enumDeclared: boolean;
  enumNative: boolean;
  realFkTarget?: { schema: string; table: string; column: string };
  looseRelCandidates: readonly LooseRelationship[];
  sourceRow: Record<string, unknown> | undefined;
  /**
   * Host relationship / metadata port (Azure Table, S4 review C, M7): the FK
   * row picker and the lookup-enum label column read through it, never
   * through SQL metadata or `fetchTableExplorerRows`. Absent = SQL, unchanged.
   */
  relationshipPort?: TableExplorerRelationshipPort;
};

type EditableCellProps = {
  value: unknown;
  column: TableExplorerColumn;
  isEditing: boolean;
  isPending: boolean;
  onStartEdit: () => void;
  onConfirm: (newValue: unknown) => void;
  onCancel: () => void;
  disabled?: boolean;
  /** Callback for opening expanded edit dialog (JSON/multiline text) */
  onExpandEdit?: (value: unknown, mode: ExpandedEditMode) => void;
  /** Optional smart-editor context (enum / FK metadata for this cell). */
  smartContext?: EditableCellContext;
  valueCodec?: TableExplorerCellValueCodec;
  valueTag?: string;
};

/**
 * Determine the input type based on column data type
 */
function getInputType(dataType: string): 'text' | 'number' | 'boolean' | 'datetime' {
  const lowerType = dataType.toLowerCase();

  if (lowerType.includes('bit') || lowerType.includes('bool')) {
    return 'boolean';
  }
  if (
    lowerType.includes('int') ||
    lowerType.includes('decimal') ||
    lowerType.includes('numeric') ||
    lowerType.includes('float') ||
    lowerType.includes('real') ||
    lowerType.includes('money')
  ) {
    return 'number';
  }
  if (
    lowerType.includes('date') ||
    lowerType.includes('time') ||
    lowerType.includes('timestamp')
  ) {
    return 'datetime';
  }

  return 'text';
}

/**
 * Format value for display
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (value instanceof Date) {
      return safeToDatetimeLocal(value);
    }
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Parse input value based on column type
 */
function parseValue(inputValue: string, inputType: 'text' | 'number' | 'boolean' | 'datetime'): unknown {
  if (inputValue === '' || inputValue === 'NULL') return null;

  switch (inputType) {
    case 'number':
      const num = Number(inputValue);
      return isNaN(num) ? inputValue : num;
    case 'boolean':
      return inputValue === 'true' || inputValue === '1';
    case 'datetime':
      return inputValue ? new Date(inputValue) : null;
    default:
      return inputValue;
  }
}

export function EditableCell({
  value,
  column,
  isEditing,
  isPending,
  onStartEdit,
  onConfirm,
  onCancel,
  disabled = false,
  onExpandEdit,
  smartContext,
  valueCodec,
  valueTag,
}: EditableCellProps) {
  const inputType = valueCodec?.inputKind ? valueCodec.inputKind(valueTag) : getInputType(column.dataType);
  const formatCellValue = useCallback(
    (next: unknown) => valueCodec ? valueCodec.format(next, valueTag) : formatValue(next),
    [valueCodec, valueTag],
  );
  const [localValue, setLocalValue] = useState<string>(() => formatCellValue(value));
  // A host codec's refusal of the typed text (S4 review F, F2): the editor stays open and says why. SQL never sets it.
  const [parseError, setParseError] = useState<string>();
  const commitTyped = useCallback(
    (text: string) => {
      if (!valueCodec) { onConfirm(parseValue(text, inputType)); return; }
      const parsed = valueCodec.parse(text, valueTag, { empty: text === '' });
      if (!parsed.ok) { setParseError(parsed.error); return; }
      setParseError(undefined);
      onConfirm(parsed.value);
    },
    [valueCodec, valueTag, inputType, onConfirm],
  );
  // A smart editor commits the picked key / enum value as it knows it (text);
  // a host codec turns it into the cell's own type (Int64 → bigint, …) exactly
  // as a typed edit would (S4 review C, M7), with the typed edit's refusal state
  // (review G, G5), and a smart clear (NULL) is an empty cell — the host's
  // removal value. No codec = SQL = unchanged.
  const commitSmart = useCallback(
    (next: unknown) => {
      if (!valueCodec) { onConfirm(next); return; }
      commitTyped(next === null || next === undefined ? '' : typeof next === 'string' ? next : valueCodec.format(next, valueTag));
    },
    [onConfirm, commitTyped, valueCodec, valueTag],
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Detect if this cell should use expanded editing
  const expandDetection = shouldExpandToDialog(value, column);
  const showExpandIconFlag = shouldShowExpandIcon(value, column);

  // Smart-editor variant (enum / FK). Computed every render but trivially
  // cheap. When variant === 'plain' (or no context) the existing dataType
  // path handles the cell.
  const smartVariant = useMemo(() => {
    if (!smartContext) return undefined;
    return resolveCellEditVariant({
      columnId: column.name,
      dataType: column.dataType,
      isRealForeignKey: !!smartContext.realFkTarget,
      realFkTarget: smartContext.realFkTarget,
      declaredEnumColumnIds: smartContext.enumDeclared ? new Set([column.name]) : new Set(),
      nativeEnumColumnIds: smartContext.enumNative ? new Set([column.name]) : new Set(),
      looseRelCandidates: smartContext.looseRelCandidates,
      sourceRow: smartContext.sourceRow,
      nullable: column.nullable,
    });
  }, [smartContext, column.name, column.dataType, column.nullable]);

  // Reset local value when external value changes
  useEffect(() => {
    if (!isEditing) {
      setLocalValue(formatCellValue(value));
      setParseError(undefined); // a smart editor's Escape bypasses handleKeyDown (G5); SQL never sets it
    }
  }, [value, isEditing, formatCellValue]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitTyped(localValue);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setLocalValue(formatCellValue(value));
        setParseError(undefined);
        onCancel();
      }
    },
    [localValue, value, onCancel, commitTyped, formatCellValue]
  );

  const handleBlur = useCallback(() => {
    // Confirm on blur
    commitTyped(localValue);
  }, [localValue, commitTyped]);

  const handleDoubleClick = useCallback(() => {
    if (disabled) return;

    // If content should expand to dialog and callback is provided, open dialog
    if (expandDetection.shouldExpand && onExpandEdit) {
      const mode = detectContentMode(value, column);
      onExpandEdit(value, mode);
      return;
    }

    // Otherwise, start inline editing
    onStartEdit();
  }, [disabled, expandDetection.shouldExpand, onExpandEdit, value, column, onStartEdit]);

  const handleExpandClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || !onExpandEdit) return;

    const mode = detectContentMode(value, column);
    onExpandEdit(value, mode);
  }, [disabled, onExpandEdit, value, column]);

  // Editing mode - show input
  if (isEditing) {
    // Smart editor (enum / FK) wins over dataType-driven inputs — declarations
    // are an explicit user opt-in. JSON columns short-circuit to the expanded
    // dialog up in handleDoubleClick before this branch runs.
    if (smartContext && smartVariant && smartVariant.variant !== 'plain') {
      const enumSource = smartContext.enumDeclared ? 'declared' : 'native';
      const smartInput = (
        <SmartCellInput
          variant={smartVariant}
          initialValue={value}
          connectionId={smartContext.connectionId}
          database={smartContext.database}
          engine={smartContext.engine}
          schema={smartContext.schema}
          table={smartContext.table}
          column={column.name}
          dataType={column.dataType}
          enumSource={enumSource}
          relationshipPort={smartContext.relationshipPort}
          onCommit={commitSmart}
          onCancel={onCancel}
          {...(parseError ? { invalid: true } : {})}
        />
      );
      // Host only: the refusal reason next to the picker, as for a typed edit (G5).
      return valueCodec ? <CellParseError error={parseError}><div className="w-full">{smartInput}</div></CellParseError> : smartInput;
    }

    // Boolean type - show checkbox
    if (inputType === 'boolean') {
      return (
        <div className="flex items-center justify-center h-full">
          <Checkbox
            checked={localValue === 'true' || localValue === '1'}
            onCheckedChange={(checked) => {
              onConfirm(checked);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
              }
            }}
            autoFocus
          />
        </div>
      );
    }

    // DateTime type - show datetime-local input
    if (inputType === 'datetime') {
      const dateInput = (
        <Input
          ref={inputRef}
          type="datetime-local"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          className={`h-6 text-xs ${parseError ? 'border-destructive' : 'border-primary'} bg-background px-1`}
          data-testid="editable-cell-input"
          aria-invalid={parseError ? true : undefined}
        />
      );
      return valueCodec ? <CellParseError error={parseError}>{dateInput}</CellParseError> : dateInput;
    }

    // Number or Text type
    const textInput = (
      <Input
        ref={inputRef}
        type={inputType === 'number' ? 'number' : 'text'}
        // U35 (A+B merge): cell values are data (keys, ids, JSON), not prose — no spellcheck squiggles, every engine.
        spellCheck={false}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={`h-6 text-xs ${parseError ? 'border-destructive' : 'border-primary'} bg-background px-1`}
        data-testid="editable-cell-input"
        aria-invalid={parseError ? true : undefined}
      />
    );
    return valueCodec ? <CellParseError error={parseError}>{textInput}</CellParseError> : textInput;
  }

  // Display mode
  const displayValue = value === null || value === undefined
    ? 'NULL'
    : typeof value === 'object'
      ? value instanceof Date
        ? safeToLocaleString(value)
        : JSON.stringify(value)
      : String(value);

  const isNull = value === null || value === undefined;

  return (
    <div
      className={`
        h-full flex items-center cursor-pointer group
        ${isPending ? 'bg-amber-100 dark:bg-amber-900/30' : ''}
        ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted/50'}
      `}
      onDoubleClick={handleDoubleClick}
      title={`${displayValue}${isPending ? ' (pending change)' : ''}`}
    >
      <span
        className={`
          truncate flex-1 min-w-0
          ${isNull ? 'text-muted-foreground/60 italic text-xs' : ''}
          ${isPending ? 'font-medium' : ''}
        `}
      >
        {displayValue}
      </span>
      {isPending && (
        <span className="ml-0.5 text-warning text-[10px] flex-shrink-0 leading-none">*</span>
      )}
      {/* Expand icon for JSON/multiline content */}
      {showExpandIconFlag && onExpandEdit && !disabled && (
        <button
          type="button"
          className="ml-1 h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity
                     text-muted-foreground hover:text-foreground rounded"
          onClick={handleExpandClick}
          title="Expand editor"
          aria-label="Expand editor"
          data-testid="editable-cell-expand"
        >
          <Maximize2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
